import type {
  Permission,
  ScopeKind,
} from "../../../../shared/rbac/permissions";
import type { IconName } from "../../Icon";

export const SCOPE_LABELS: Record<ScopeKind, string> = {
  tenant: "Tenant",
  space: "Spaces",
  connected_app: "Connectors",
  skill: "Skills",
  network: "Networks",
  user_group: "Groups",
  service_account: "Service accounts",
  oidc_provider: "OIDC providers",
};

export const SCOPE_ICONS: Record<ScopeKind, IconName> = {
  tenant: "building-2",
  space: "hash",
  connected_app: "api-key-custom",
  skill: "skills",
  network: "earth",
  user_group: "users-round",
  service_account: "bot",
  oidc_provider: "key",
};

const PERMISSION_LABELS: Record<Permission, string> = {
  allow_all: "Full tenant access",
  "space:read": "View spaces",
  "space:write": "Edit space workflows",
  "space:push_live": "Push workflows live",
  "space:manage": "Manage space access",
  "connected_app:read": "View connector settings",
  "connected_app:write": "Edit connector settings",
  "connected_app:share": "Manage connector access",
  "connected_app:use": "Use connectors in workflows",
  "skill:read": "View skills",
  "skill:write": "Manage skill access",
  "network:read": "View network",
  "network:write": "Manage network",
  "network:use": "Use network",
  "user_group:read": "View group members",
  "user_group:write": "Manage group access",
  "service_account:read": "View service account",
  "service_account:assume": "Get temporary credentials",
  "service_account:manage": "Manage service account",
  "oidc_provider:manage": "Manage OIDC provider",
  "tenant:connected_app_create": "Create connectors",
  "tenant:skill_create": "Create skills",
  "tenant:network_create": "Create networks",
  "tenant:service_account_create": "Create service accounts",
  "tenant:service_accounts_read": "View all service accounts",
  "tenant:connected_apps_read": "View all connectors",
  "tenant:connected_app_share": "Share connectors you create",
  "tenant:skills_read": "View all skills",
  "tenant:space_create": "Create spaces",
  "tenant:spaces_read": "View all spaces",
  "tenant:spaces_manage": "Manage all spaces",
  "tenant:user_group_create": "Create groups",
  "tenant:users_manage": "Manage members",
  "tenant:audit_logs_read": "View audit logs",
  "tenant:audit_logs_manage": "Manage audit log destinations",
  "tenant:ai_providers_manage": "Manage AI providers",
  "tenant:sso_manage": "Manage SSO",
  "tenant:oidc_providers_manage": "Manage OIDC providers",
  "tenant:feature_flags_manage": "Manage feature flags",
  "tenant:personal_chats_read": "View all personal chats",
  "tenant:roles_manage": "Manage roles",
  "tenant:networks_manage": "Manage networks",
  "tenant:egress_routes_manage": "Manage egress routes",
  "tenant:egress_rules_manage": "Manage egress rules",
};

export const PERMISSION_ICONS: Record<Permission, IconName> = {
  allow_all: "crown",
  "space:read": "eye",
  "space:write": "pencil",
  "space:push_live": "rocket",
  "space:manage": "settings-2",
  "connected_app:read": "eye",
  "connected_app:write": "pencil",
  "connected_app:share": "settings-2",
  "connected_app:use": "plug",
  "skill:read": "eye",
  "skill:write": "settings-2",
  "network:read": "earth",
  "network:write": "earth",
  "network:use": "earth",
  "user_group:read": "eye",
  "user_group:write": "settings-2",
  "service_account:read": "eye",
  "service_account:assume": "key",
  "service_account:manage": "settings-2",
  "oidc_provider:manage": "shield-check",
  "tenant:connected_app_create": "api-key-custom",
  "tenant:skill_create": "skills",
  "tenant:network_create": "earth",
  "tenant:service_account_create": "bot",
  "tenant:service_accounts_read": "bot",
  "tenant:connected_apps_read": "app-window",
  "tenant:connected_app_share": "settings-2",
  "tenant:skills_read": "book",
  "tenant:space_create": "hash",
  "tenant:spaces_read": "gallery-vertical-end",
  "tenant:spaces_manage": "settings-2",
  "tenant:user_group_create": "users-round",
  "tenant:users_manage": "id-card",
  "tenant:audit_logs_read": "table",
  "tenant:audit_logs_manage": "logs",
  "tenant:ai_providers_manage": "brain",
  "tenant:sso_manage": "shield-check",
  "tenant:oidc_providers_manage": "shield-check",
  "tenant:feature_flags_manage": "ticket",
  "tenant:personal_chats_read": "ask",
  "tenant:roles_manage": "id-card-lanyard",
  "tenant:networks_manage": "earth",
  "tenant:egress_routes_manage": "pin",
  "tenant:egress_rules_manage": "shield-check",
};

export const PERMISSION_DESCRIPTIONS: Partial<Record<Permission, string>> = {
  "space:read": "Open the space and view its workflows.",
  "space:write": "Create, edit, and run workflows in the space.",
  "space:push_live":
    "Push drafts live and take live workflows out of operation (restore, move, archive, pause).",
  "space:manage":
    "Change settings, manage access, and permanently delete workflows.",
  "connected_app:read": "View the connector and its non-secret settings.",
  "connected_app:write": "Change the connector's settings and credentials.",
  "connected_app:share": "Manage who has access to the connector.",
  "connected_app:use":
    "Use the connector's credentials in workflows and build chats.",
  "skill:read": "View the skill and its instructions.",
  "skill:write": "Change instructions and manage access.",
  "network:read": "View the network and its destinations.",
  "network:write": "Change settings and manage access.",
  "network:use": "Add egress routes that send traffic through the network.",
  "user_group:read": "View the group and its members.",
  "user_group:write": "Change membership and access.",
  "service_account:read":
    "View the service account, its credentials, and its grants.",
  "service_account:assume":
    "Get a short-lived credential that acts as the service account.",
  "service_account:manage":
    "Manage the service account: issue and revoke credentials, change grants, and delete it.",
  "tenant:connected_app_create": "Create new connectors.",
  "tenant:skill_create": "Create new skills.",
  "tenant:network_create": "Create new networks.",
  "tenant:service_account_create": "Create new service accounts.",
  "tenant:service_accounts_read": "View every service account in the tenant.",
  "tenant:connected_apps_read": "View every connector in the tenant.",
  "tenant:connected_app_share":
    "When creating a connector, also gives the creator permission to manage its access.",
  "tenant:skills_read": "View every skill in the tenant.",
  "tenant:space_create": "Create new spaces.",
  "tenant:spaces_read": "View every space in the tenant.",
  "tenant:spaces_manage":
    "View, edit, and manage access on every space in the tenant.",
  "tenant:user_group_create": "Create new groups.",
  "tenant:users_manage": "Invite, deactivate, and reactivate members.",
  "tenant:audit_logs_read": "View tenant audit events.",
  "tenant:audit_logs_manage":
    "Configure where audit logs are shipped for retention and SIEM ingestion.",
  "tenant:ai_providers_manage": "Configure AI providers.",
  "tenant:sso_manage": "Configure SSO and group mappings.",
  "tenant:oidc_providers_manage":
    "Register OIDC identity providers for workload federation.",
  "tenant:feature_flags_manage": "Enable and disable feature flags.",
  "tenant:personal_chats_read": "View every personal chat in the tenant.",
  "tenant:roles_manage": "Create, delete, and assign roles.",
  "tenant:networks_manage": "View and manage networks.",
  "tenant:egress_routes_manage":
    "Add, edit, and remove routes in the tenant routing table.",
  "tenant:egress_rules_manage":
    "Add, edit, and remove egress access-control rules.",
};

export function permissionLabel(p: Permission): string {
  return PERMISSION_LABELS[p] ?? p;
}
